import React, { useState } from "react";
import { Button, Modal, Space, Typography, Alert, Spin } from "antd";
import { BugOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useFrontendDocumentService } from "../services/frontendDocumentService";

const { Text, Paragraph, Title } = Typography;

export const ApiTestButton: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const { getDocuments, createDocument } = useFrontendDocumentService();

  const runTest = async (testName: string, testFn: () => Promise<any>) => {
    setResults(prev => [...prev, { name: testName, status: 'running', message: 'Test läuft...' }]);
    
    try {
      const result = await testFn();
      setResults(prev => prev.map(r => 
        r.name === testName 
          ? { ...r, status: 'success', message: 'Test erfolgreich', data: result }
          : r
      ));
    } catch (error: any) {
      setResults(prev => prev.map(r => 
        r.name === testName 
          ? { ...r, status: 'error', message: error.message || 'Test fehlgeschlagen', error }
          : r
      ));
    }
  };

  const runAllTests = async () => {
    setTesting(true);
    setResults([]);

    // Test 1: Documents laden
    await runTest('Documents laden', async () => {
      const docs = await getDocuments();
      return { count: docs.length, sample: docs.slice(0, 2) };
    });

    // Test 2: Document erstellen
    await runTest('Test Document erstellen', async () => {
      const testDoc = await createDocument({
        type: 'offer',
        emailFieldCustomer: 'test@beispiel.de',
        positions: [
          {
            description: 'Test Position',
            quantity: 1,
            price: 100,
            total: 100
          }
        ],
        calculation: {
          subtotal: 100,
          tax: 19,
          total: 119
        }
      });
      return testDoc;
    });

    // Test 3: Documents nach Test neu laden
    await runTest('Documents nach Erstellung laden', async () => {
      const docs = await getDocuments();
      return { count: docs.length };
    });

    setTesting(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Spin size="small" />;
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Button
        icon={<BugOutlined />}
        onClick={() => setVisible(true)}
        size="small"
        style={{ marginLeft: 8 }}
      >
        API Test
      </Button>

      <Modal
        title="Frontend Document API Test"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVisible(false)}>
            Schließen
          </Button>,
          <Button
            key="test"
            type="primary"
            loading={testing}
            onClick={runAllTests}
            disabled={testing}
          >
            Tests ausführen
          </Button>,
        ]}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message="API-Test"
            description="Dieser Test überprüft die Funktionalität der neuen Frontend Document API."
            type="info"
            showIcon
          />

          {results.length > 0 && (
            <div>
              <Title level={5}>Test-Ergebnisse:</Title>
              {results.map((result, index) => (
                <div key={index} style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                  <Space>
                    {getStatusIcon(result.status)}
                    <Text strong>{result.name}</Text>
                    <Text type={result.status === 'error' ? 'danger' : result.status === 'success' ? 'success' : undefined}>
                      {result.message}
                    </Text>
                  </Space>
                  
                  {result.data && (
                    <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                      <Text code style={{ fontSize: 12 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Text>
                    </Paragraph>
                  )}
                  
                  {result.error && (
                    <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                      <Text type="danger" code style={{ fontSize: 12 }}>
                        {JSON.stringify(result.error, null, 2)}
                      </Text>
                    </Paragraph>
                  )}
                </div>
              ))}
            </div>
          )}

          {results.length === 0 && (
            <Alert
              message="Bereit zum Testen"
              description="Klicken Sie auf 'Tests ausführen', um die API-Endpunkte zu testen."
              type="warning"
            />
          )}
        </Space>
      </Modal>
    </>
  );
}; 